import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, Users, CheckSquare, Activity } from 'lucide-react';

export const Statistics = () => {
  const [taskStats, setTaskStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/stats/tasks');
      setTaskStats(response.data);
    } catch (error) {
      toast.error('Errore caricamento statistiche');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const pieData = taskStats.map(stat => ({
    name: stat.dipendente,
    value: stat.completati
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 md:p-8 lg:p-10" data-testid="statistics-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2">
          Statistiche
        </h1>
        <p className="text-base text-slate-600">
          Analisi delle performance del team
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <Users size={24} className="text-blue-600" />
            </div>
            <TrendingUp size={16} className="text-slate-400" />
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">{taskStats.length}</p>
          <p className="text-sm text-slate-600">Dipendenti Attivi</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-green-50">
              <CheckSquare size={24} className="text-green-600" />
            </div>
            <TrendingUp size={16} className="text-slate-400" />
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">
            {taskStats.reduce((sum, stat) => sum + stat.completati, 0)}
          </p>
          <p className="text-sm text-slate-600">Task Completati</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-orange-50">
              <Activity size={24} className="text-orange-600" />
            </div>
            <TrendingUp size={16} className="text-slate-400" />
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">
            {taskStats.reduce((sum, stat) => sum + stat.in_corso, 0)}
          </p>
          <p className="text-sm text-slate-600">Task In Corso</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-lg bg-purple-50">
              <TrendingUp size={24} className="text-purple-600" />
            </div>
            <TrendingUp size={16} className="text-slate-400" />
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">
            {taskStats.length > 0 && taskStats.reduce((sum, stat) => sum + stat.totale, 0) > 0
              ? Math.round(
                  (taskStats.reduce((sum, stat) => sum + stat.completati, 0) /
                    taskStats.reduce((sum, stat) => sum + stat.totale, 0)) *
                    100
                )
              : 0}
            %
          </p>
          <p className="text-sm text-slate-600">Tasso Completamento</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            Task per Dipendente
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={taskStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="dipendente"
                tick={{ fill: '#64748b', fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '14px' }} />
              <Bar dataKey="completati" fill="#10B981" name="Completati" radius={[8, 8, 0, 0]} />
              <Bar dataKey="in_corso" fill="#2563EB" name="In Corso" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            Distribuzione Task Completati
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {taskStats.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Activity size={48} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Nessun dato disponibile
          </h3>
          <p className="text-slate-600">
            Le statistiche appariranno quando ci saranno task completati
          </p>
        </div>
      )}
    </div>
  );
};